import React, { FC, useEffect, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { infer as Infer, number, object, string, date, ZodIssueCode } from 'zod'
import { Box, Checkbox, Grid, Typography } from '@mui/material'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import {
  ItemEntry,
  EntryType,
  ItemEntryDetail,
  createEwayBill,
  getGSTNDetails,
} from '../../../../services'

import ItemListBox, {
  sumCgstValue,
  sumIgstValue,
  sumSgstValue,
  sumTaxableAmount,
  sumTotalValue,
  totalValue,
} from './ItemListBox'

import {
  addLeadingZero,
  getFormattedDate,
  gstinRegex,
  noEmptyRegex,
  pincodeRegex,
  vehicleNumberRegex,
} from '../../../../utils'

import { states } from '../../../../utils/appUtils'

// Components
import {
  AppDialog,
  FormInputAutosuggest,
  FormInputDateField,
  FormInputTextField,
  Loader,
} from '../../../../components'
import { showAlertAction } from '../../../../store/alerts'

// Redux
import { useAppDispatch, useAppSelector } from '../../../../hooks'
import { fetchSlipsListAction } from '../../../../store/slips'
import { EwayInfoResponse, EwayItem, getEwayInfo } from './Services'

const preFillEWayBillFromeData = (gstinData, setValue) => {
  if (gstinData) {
    setValue('billFromTradName', gstinData.tradeName)
    setValue('billFromGSTIN', gstinData.gstin)
    if (gstinData.stateCode) {
      setValue('billFromState', addLeadingZero(gstinData.stateCode))
    }
  }
}

const preFillEWayBillToData = (gstinData, setValue) => {
  // Check if userData is available and contains the relevant data
  if (gstinData) {
    setValue('billToTradeName', gstinData.tradeName)
    setValue('billToGSTIN', gstinData.gstin)

    if (gstinData.stateCode) {
      setValue('billToState', addLeadingZero(gstinData.stateCode))
      setValue('shipToState', addLeadingZero(gstinData.stateCode))
    }
  }
}

const BillFromDispatchFromBox = ({
  entryType,
  registerFieldRef,
}: {
  entryType: string
  registerFieldRef?: (name: string, el: HTMLDivElement | null) => void
}) => {
  const { control, watch } = useFormContext()
  const billFromGstin = String(watch('billFromGSTIN') ?? '')
  const isBillFromUrp = billFromGstin.trim().toUpperCase() === 'URP'
  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Grid container direction={'column'} spacing={2}>
          <Grid item>
            <Typography
              variant="h6"
              sx={{
                mt: 4,
                fontWeight: 'bold',
              }}
            >
              Bill From
            </Typography>
          </Grid>
          <Grid item>
            <Box
              ref={(el: HTMLDivElement | null) =>
                registerFieldRef?.('billFromTradName', el)
              }
            >
              <FormInputTextField
                control={control}
                name={'billFromTradName'}
                label={'Name'}
                InputProps={{
                  readOnly: !isBillFromUrp,
                }}
                required
              />
            </Box>
          </Grid>
          <Grid item>
            <Box
              ref={(el: HTMLDivElement | null) =>
                registerFieldRef?.('billFromGSTIN', el)
              }
            >
              <FormInputTextField
                control={control}
                name={'billFromGSTIN'}
                label={'GSTIN'}
                InputProps={{
                  readOnly: false,
                }}
                required
              />
            </Box>
          </Grid>
          <Grid item>
            <Box
              ref={(el: HTMLDivElement | null) =>
                registerFieldRef?.('billFromState', el)
              }
            >
              <FormInputAutosuggest
                control={control}
                name={'billFromState'}
                label="Select State"
                readOnly={!isBillFromUrp}
                options={states.map((s) => {
                  return {
                    label: s.label,
                    id: s.code,
                  }
                })}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Grid container direction={'column'} spacing={2}>
          <Grid item>
            <Typography
              variant="h6"
              sx={{
                mt: 4,
                fontWeight: 'bold', // Make the text bold
              }}
            >
              Dispatch From
            </Typography>
          </Grid>
          <Grid item>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box
                  ref={(el: HTMLDivElement | null) =>
                    registerFieldRef?.('dispatchFromAddress1', el)
                  }
                >
                  <FormInputTextField
                    control={control}
                    name={'dispatchFromAddress1'}
                    label={'Address 1'}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  ref={(el: HTMLDivElement | null) =>
                    registerFieldRef?.('dispatchFromAddress2', el)
                  }
                >
                  <FormInputTextField
                    control={control}
                    name={'dispatchFromAddress2'}
                    label={'Address 2'}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Box
              ref={(el: HTMLDivElement | null) =>
                registerFieldRef?.('dispatchFromPlace', el)
              }
            >
              <FormInputTextField
                control={control}
                name={'dispatchFromPlace'}
                label={'Place'}
              />
            </Box>
          </Grid>
          <Grid item>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box
                  ref={(el: HTMLDivElement | null) =>
                    registerFieldRef?.('dispatchFromPincode', el)
                  }
                >
                  <FormInputTextField
                    control={control}
                    name={'dispatchFromPincode'}
                    label={'Pincode'}
                    required
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  ref={(el: HTMLDivElement | null) =>
                    registerFieldRef?.('dispatchFromState', el)
                  }
                >
                  <FormInputAutosuggest
                    control={control}
                    name={'dispatchFromState'}
                    label="State"
                    options={states.map((s) => {
                      return {
                        label: s.label,
                        id: s.code,
                      }
                    })}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

const BillToShipToBox = ({
  entryType,
  registerFieldRef,
}: {
  entryType: string
  registerFieldRef?: (name: string, el: HTMLDivElement | null) => void
}) => {
  const { control, watch, setValue } = useFormContext()
  const billToGstin = String(watch('billToGSTIN') ?? '')
  const isBillToUrp = billToGstin.trim().toUpperCase() === 'URP'
  const handleShipToGSTINChange = (newValue) => {
    const upperCaseValue = newValue.target.value.toUpperCase()
    setValue('shipToGSTIN', upperCaseValue, { shouldValidate: true })
  }
  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Grid container direction={'column'} spacing={2}>
          <Grid item>
            <Typography
              variant="h6"
              sx={{
                mt: 4,
                fontWeight: 'bold', // Make the text bold
              }}
            >
              Bill To
            </Typography>
          </Grid>
          <Grid item>
            <Box
              ref={(el: HTMLDivElement | null) =>
                registerFieldRef?.('billToTradeName', el)
              }
            >
              <FormInputTextField
                control={control}
                name={'billToTradeName'}
                label={'Name'}
                InputProps={{
                  readOnly: !isBillToUrp,
                }}
                required
              />
            </Box>
          </Grid>
          <Grid item>
            <Box
              ref={(el: HTMLDivElement | null) =>
                registerFieldRef?.('billToGSTIN', el)
              }
            >
              <FormInputTextField
                control={control}
                name={'billToGSTIN'}
                label={'GSTIN'}
                InputProps={{
                  readOnly: false,
                }}
                required
              />
            </Box>
          </Grid>
          <Grid item>
            <Box
              ref={(el: HTMLDivElement | null) =>
                registerFieldRef?.('billToState', el)
              }
            >
              <FormInputAutosuggest
                control={control}
                name={'billToState'}
                label="State"
                readOnly={!isBillToUrp}
                options={states.map((s) => {
                  return {
                    label: s.label,
                    id: s.code,
                  }
                })}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Grid container direction={'column'} spacing={2}>
          <Grid item>
            <Typography
              variant="h6"
              sx={{
                mt: 4,
                fontWeight: 'bold', // Make the text bold
              }}
            >
              Ship To
            </Typography>
          </Grid>
          <Grid item>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box
                  ref={(el: HTMLDivElement | null) =>
                    registerFieldRef?.('shipToAddress1', el)
                  }
                >
                  <FormInputTextField
                    control={control}
                    name={'shipToAddress1'}
                    label={'Address 1'}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  ref={(el: HTMLDivElement | null) =>
                    registerFieldRef?.('shipToAddress2', el)
                  }
                >
                  <FormInputTextField
                    control={control}
                    name={'shipToAddress2'}
                    label={'Address 2'}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container spacing={2}>
              <Grid
                item
                xs={
                  watch('transactionType') === 2 ||
                  watch('transactionType') === 4
                    ? 6
                    : 12
                }
              >
                <Box
                  ref={(el: HTMLDivElement | null) =>
                    registerFieldRef?.('shipToPlace', el)
                  }
                >
                  <FormInputTextField
                    control={control}
                    name={'shipToPlace'}
                    label={'Place'}
                  />
                </Box>
              </Grid>
              {(watch('transactionType') === 2 ||
                watch('transactionType') === 4) && (
                <Grid item xs={6}>
                  <Box
                    ref={(el: HTMLDivElement | null) =>
                      registerFieldRef?.('shipToGSTIN', el)
                    }
                  >
                    <FormInputTextField
                      control={control}
                      name={'shipToGSTIN'}
                      label={'Ship-To GSTIN'}
                      onChange={handleShipToGSTINChange}
                      InputProps={{ inputProps: { maxLength: 15, minLength: 3 } }}
                      required
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box
                  ref={(el: HTMLDivElement | null) =>
                    registerFieldRef?.('shipToPincode', el)
                  }
                >
                  <FormInputTextField
                    control={control}
                    name={'shipToPincode'}
                    label={'Pincode'}
                    required
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  ref={(el: HTMLDivElement | null) =>
                    registerFieldRef?.('shipToState', el)
                  }
                >
                  <FormInputAutosuggest
                    control={control}
                    name={'shipToState'}
                    label="State"
                    options={states.map((s) => {
                      return {
                        label: s.label,
                        id: s.code,
                      }
                    })}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

// Transaction Type Array
const transactionTypes = [
  { id: 1, label: 'Regular' },
  { id: 2, label: 'Bill To - Ship To' },
  { id: 3, label: 'Bill from - Dispatch From' },
  { id: 4, label: 'Combination' },
]

/** e-Way bill API limits (Addr1 / Addr2 / Place) */
const EWAY_ADDRESS_LINE_MAX = 100
const EWAY_PLACE_MAX = 50

// Formschema
const schema = object({
  transactionType: number({
    required_error: 'Please select a transaction type',
  }),
  billFromTradName: string({ required_error: 'Please enter trade name' }),
  docNo: string({ required_error: 'Please enter document number' }).regex(
    noEmptyRegex,
    'Please enter document number'
  ),
  docDate: date({ required_error: 'Please enter document date' }),
  billFromGSTIN: string({ required_error: 'Please enter GST number' }).regex(
    gstinRegex,
    'Please provide valid GSTIN'
  ),
  billFromState: string({ required_error: 'Please select a state' }).regex(
    noEmptyRegex,
    'Please select state'
  ),
  dispatchFromAddress1: string()
    .max(
      EWAY_ADDRESS_LINE_MAX,
      `Address line 1 must be at most ${EWAY_ADDRESS_LINE_MAX} characters`
    )
    .optional(),
  dispatchFromAddress2: string()
    .max(
      EWAY_ADDRESS_LINE_MAX,
      `Address line 2 must be at most ${EWAY_ADDRESS_LINE_MAX} characters`
    )
    .optional(),
  dispatchFromPlace: string({ required_error: 'Please enter place name' })
    .max(EWAY_PLACE_MAX, `Place must be at most ${EWAY_PLACE_MAX} characters`)
    .regex(noEmptyRegex, 'Please enter place name'),
  dispatchFromPincode: string({ required_error: 'Please enter pincode' }).regex(
    pincodeRegex,
    'Please provide valid pincode'
  ),
  dispatchFromState: string({ required_error: 'Please select a state ' }).regex(
    noEmptyRegex,
    'Please select state'
  ),
  billToTradeName: string({ required_error: 'Please enter trade name' }).regex(
    noEmptyRegex,
    'Please enter trade name'
  ),
  billToGSTIN: string({ required_error: 'Please enter GSTIN number' }).regex(
    gstinRegex,
    'Please provide valid GSTIN'
  ),
  billToState: string({ required_error: 'Please select state ' }).regex(
    noEmptyRegex,
    'Please select state'
  ),
  shipToAddress1: string()
    .max(
      EWAY_ADDRESS_LINE_MAX,
      `Address line 1 must be at most ${EWAY_ADDRESS_LINE_MAX} characters`
    )
    .optional(),
  shipToAddress2: string()
    .max(
      EWAY_ADDRESS_LINE_MAX,
      `Address line 2 must be at most ${EWAY_ADDRESS_LINE_MAX} characters`
    )
    .optional(),
  shipToPlace: string({ required_error: 'Please enter place name' })
    .max(EWAY_PLACE_MAX, `Place must be at most ${EWAY_PLACE_MAX} characters`)
    .regex(noEmptyRegex, 'Please enter place name'),
  shipToPincode: string({ required_error: 'Please enter pincode' }).regex(
    pincodeRegex,
    'Please provide valide pincode'
  ),
  shipToState: string({ required_error: 'Please select state ' }).regex(
    noEmptyRegex,
    'Please select state'
  ),
  shipToGSTIN: string().optional(),
  transporterId: string()
    .regex(gstinRegex, 'Please provide valid transporter Id')
    .optional(),
  transporterName: string().optional(),
  distance: string().optional(),
  vehicleNumber: string()
    .regex(vehicleNumberRegex, 'Please provide valid vehicle number')
    .optional(),
  transporterDoc: string().optional(),
  transportDate: date().optional(),
}).superRefine((values, ctx) => {
  if (
    (values.transactionType === 2 || values.transactionType === 4) &&
    (!values.shipToGSTIN || !values.shipToGSTIN.trim())
  ) {
    ctx.addIssue({
      message: 'Ship-To GSTIN is required',
      code: ZodIssueCode.custom,
      path: ['shipToGSTIN'],
    })
  } else if (values.shipToGSTIN && !gstinRegex.test(values.shipToGSTIN.toUpperCase())) {
    ctx.addIssue({
      message: 'Please provide valid GSTIN or URP',
      code: ZodIssueCode.custom,
      path: ['shipToGSTIN'],
    })
  }

  if (!values.transporterId && !values.vehicleNumber) {
    ctx.addIssue({
      message: 'transporter id   OR   vehicle no  required',
      code: ZodIssueCode.custom,
      path: ['vehicleNumber'],
    })

    ctx.addIssue({
      message: 'vehicle no   OR   transporter id  required',
      code: ZodIssueCode.custom,
      path: ['transporterId'],
    })
  }
})

type Schema = Infer<typeof schema>

export interface CreateEwayBillProps {
  children?: React.ReactNode
  onClose?: (string) => void
  slip: ItemEntryDetail
  accountId: number
  entryType: string
}

export const CreateEwayBill: FC<CreateEwayBillProps> = ({
  onClose,
  slip,
  accountId,
  entryType,
}) => {
  const { data: userData } = useAppSelector((state) => state.userData)
  const { data: slipsData } = useAppSelector((state) => state.manageSlips)
  const reachHookForm = useForm<Schema>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {
      transactionType: transactionTypes[0].id,
      billFromState: states[0].code,
      dispatchFromState: states[0].code,
      billToState: states[0].code,
      shipToState: states[0].code,
    },
  })

  const { control, handleSubmit, watch, setValue } = reachHookForm
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const itemListSectionRef = useRef<HTMLDivElement>(null)
  const [loadingStore, setLoadingStore] = useState(false)
  const [loadingCustomer, setLoadingCustomer] = useState(false)
  const [loadingEwayInfo, setLoadingEwayInfo] = useState(false)
  const [creatingEWayBill, setCreatingEWayBill] = useState(false)
  const [ewayItemList, setEwayItemList] = useState<EwayItem[]>([])
  const [ewayInfo, setEwayInfo] = useState<EwayInfoResponse | null>(null)
  const [selectedTransactionId, setSelectedTransactionId] = useState<number>(1)
  const [gstinData, setGstinData] = useState(null)
  const [custGstinData, setCustGstinData] = useState(null)

  const isInitialLoad = useRef(true)
  const dispatchAction = useAppDispatch()

  useEffect(() => {
    if (!slip?.challanId) return
    setLoadingEwayInfo(true)
    getEwayInfo(slip.challanId, selectedTransactionId).then((response) => {
      if (response.data?.success && response.data?.data) {
        const data = response.data.data
        setEwayInfo(data)
        setValue('docNo', data.documentNo)
        setValue('docDate', new Date(data.documentDate))
        setValue('vehicleNumber', data.vehicleNo?.replaceAll?.(' ', ''))

        if (isInitialLoad.current && data.transactionType) {
          setValue('transactionType', data.transactionType)
          setSelectedTransactionId(data.transactionType)
          isInitialLoad.current = false
        }

        const bFrom = data.billFrom
        if (bFrom) {
          setValue('billFromTradName', bFrom.tradeName)
          setValue('billFromGSTIN', bFrom.gstin)
          if (bFrom.stateCode) {
            setValue('billFromState', addLeadingZero(bFrom.stateCode))
          }
        }
        const bTo = data.billTo
        if (bTo) {
          setValue('billToTradeName', bTo.tradeName)
          setValue('billToGSTIN', bTo.gstin)
          if (bTo.stateCode) {
            setValue('billToState', addLeadingZero(bTo.stateCode))
          }
        }
        if (data.shipToGstin) {
          setValue('shipToGSTIN', data.shipToGstin)
        }
        const from = data.dispatchFrom
        if (from) {
          setValue('dispatchFromAddress1', from.address1)
          setValue('dispatchFromAddress2', from.address2)
          setValue('dispatchFromPlace', from.place)
          setValue(
            'dispatchFromPincode',
            from.pincode != null && from.pincode !== ''
              ? String(from.pincode)
              : ''
          )
          setValue('dispatchFromState', from.stateCode)
        }
        const to = data.shipTo || data.dispatchTo
        if (to) {
          setValue('shipToAddress1', to.address1)
          setValue('shipToAddress2', to.address2)
          setValue('shipToPlace', to.place)
          setValue(
            'shipToPincode',
            to.pincode != null && to.pincode !== '' ? String(to.pincode) : ''
          )
          setValue('shipToState', to.stateCode)
        }
        if (data.transporterId !== undefined && data.transporterId !== null) {
          setValue('transporterId', data.transporterId)
        }
        if (
          data.transporterName !== undefined &&
          data.transporterName !== null
        ) {
          setValue('transporterName', data.transporterName)
        }
        if (data.transportDocNo !== undefined && data.transportDocNo !== null) {
          setValue('transporterDoc', data.transportDocNo)
        }
        setEwayItemList(data.itemList ?? [])
      }
      setLoadingEwayInfo(false)
    })
  }, [slip?.challanId, selectedTransactionId])

  const validateEwayItems = () => {
    if (ewayItemList.length == 0) {
      return false
    }
    for (let i = 0; i < ewayItemList.length; i++) {
      const item = ewayItemList[i]
      if (
        !item.productName ||
        !item.hsnCode ||
        item.quantity <= 0 ||
        item.taxableAmount <= 0
      ) {
        return false
      }
    }
    return true
  }

  const scrollToFirstError = (errors: Record<string, unknown>) => {
    const firstErrorKey = Object.keys(errors)[0]
    if (!firstErrorKey) return
    fieldRefs.current[firstErrorKey]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
    const focusable = fieldRefs.current[firstErrorKey]?.querySelector?.(
      'input, [role="combobox"]'
    ) as HTMLInputElement | null
    focusable?.focus()
  }

  const submitHandle = handleSubmit(
    (data: Schema) => {
      if (!validateEwayItems()) {
        dispatchAction(
          showAlertAction({
            message:
              'Data validation errors, Please scroll and check ITEM LIST section.',
            severity: 'error',
          })
        )
        itemListSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
        return
      }

      // Submit the form or perform the desired action

      const billFrom = {
        name: data.billFromTradName,
        gstin: data.billFromGSTIN,
        state: data.billFromState,
      }

      const billTo = {
        name: data.billToTradeName,
        gstin: data.billToGSTIN,
        state: data.billToState,
      }

      const distance: number = parseInt(data.distance) || 0

      const formattedDate = getFormattedDate(data.transportDate)
      const transDocDate: string | null =
        formattedDate !== '' ? formattedDate : null

      const docDate = getFormattedDate(data.docDate)

      const dispatchFrom = {
        address1: data.dispatchFromAddress1,
        address2: data.dispatchFromAddress2,
        place: data.dispatchFromPlace,
        pincode: data.dispatchFromPincode,
        state: data.dispatchFromState,
      }

      const shipTo = {
        address1: data.shipToAddress1,
        address2: data.shipToAddress2,
        place: data.shipToPlace,
        pincode: data.shipToPincode,
        state: data.shipToState,
      }

      const request = {
        accountId: accountId,
        challanId: slip.challanId,
        challanNumber: slip.displayCHL.toString(),
        entryType: ewayInfo?.challanType ?? entryType,
        transactionType: data.transactionType,
        shipToGSTIN: data.shipToGSTIN,
        distance: distance,
        vehicleNo: data.vehicleNumber,
        billFrom: billFrom,
        billTo: billTo,
        dispatchFrom: dispatchFrom,
        shipTo: shipTo,
        transporterId: data.transporterId,
        transporterName: data.transporterName,
        transDocNo: data.transporterDoc,
        transDocDate: transDocDate,
        itemList: ewayItemList,
        totalValue: sumTaxableAmount(ewayItemList),
        totInvoiceValue: sumTotalValue(ewayItemList),
        cgstValue: sumCgstValue(ewayItemList),
        sgstValue: sumSgstValue(ewayItemList),
        igstValue: sumIgstValue(ewayItemList),
        docDate: docDate,
      }

      console.log(request)
      setCreatingEWayBill(true)
      createEwayBill(request).then((response) => {
        setCreatingEWayBill(false)
        if (response.data.success) {
          showAlertAction({
            message: 'EWay bill created successfully',
            severity: 'success',
          })
          dispatchAction(fetchSlipsListAction(accountId))
          onClose(response.data.data.ewayBillNo)
        } else if (response.data.message) {
          dispatchAction(
            showAlertAction({
              message: response.data?.message,
              severity: 'error',
            })
          )
        }
      })
    },
    (formErrors) => {
      setTimeout(() => scrollToFirstError(formErrors), 0)
    }
  )

  // This function will be called whenever the value of the watched input changes
  const handleTrsanporterIDChange = async (e) => {
    const value = e.target.value
    // Clear error for transporterName field
    setValue('transporterName', '') // Clear the value first
    const upperCaseValue = value.toUpperCase()
    setValue('transporterId', upperCaseValue)
    const isValidInput = /^([0-9]{2}[A-Z0-9]{13})|URP$/.test(value)
    if (!isValidInput) {
      return
    }
    getGSTNDetails(value).then((response) => {
      if (response.data.success && response.data.data.tradeName) {
        // fill transporter name
        setValue('transporterName', response.data.data.tradeName)
      }
    })
  }

  const handleVehicleNumberChange = (newValue) => {
    // Convert the vehicle number to uppercase
    const upperCaseValue = newValue.target.value.toUpperCase()
    setValue('vehicleNumber', upperCaseValue)
  }

  const onSelectTrasactionType = (option) => {
    setSelectedTransactionId(option.id)
  }

  function resetDispatchFromFields() {
    setValue('dispatchFromAddress1', '')
    setValue('dispatchFromAddress2', '')
    setValue('dispatchFromPlace', '')
    setValue('dispatchFromPincode', '')
    setValue('dispatchFromState', '')
  }

  function resetShipToFields() {
    setValue('shipToAddress1', '')
    setValue('shipToAddress2', '')
    setValue('shipToPlace', '')
    setValue('shipToPincode', '')
    setValue('shipToState', '')
  }

  return (
    <AppDialog
      id="edit-item"
      title={`eWay Bill Form: ${
        ewayInfo?.challanType === EntryType.IN ? 'Inward' : 'Outward'
      }`}
      submitButtonText="Submit"
      onClose={() => {
        onClose(null)
      }}
      onSubmit={submitHandle}
      close={false}
      maxWidth="lg"
    >
      {/* Loader overlay */}
      <FormProvider {...reachHookForm}>
        <form>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 'bold', // Make the text bold
            }}
          >
            TRASACTION DETAILS
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box
                ref={(el: HTMLDivElement | null) => {
                  fieldRefs.current['transactionType'] = el
                }}
              >
                <FormInputAutosuggest
                  control={control}
                  name={'transactionType'}
                  label="Select transaction type"
                  options={transactionTypes}
                  onSelect={onSelectTrasactionType}
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box
                ref={(el: HTMLDivElement | null) => {
                  fieldRefs.current['docNo'] = el
                }}
              >
                <FormInputTextField
                  control={control}
                  name={'docNo'}
                  value={slip.displayCHL}
                  label={'Document No'}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box
                ref={(el: HTMLDivElement | null) => {
                  fieldRefs.current['docDate'] = el
                }}
              >
                <FormInputDateField
                  format="dd/MM/yyyy"
                  control={control}
                  name={'docDate'}
                  label="Document Date"
                />
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <div key={1}>
              <BillFromDispatchFromBox
                entryType={entryType}
                registerFieldRef={(name, el) => {
                  fieldRefs.current[name] = el
                }}
              />
              <BillToShipToBox
                entryType={entryType}
                registerFieldRef={(name, el) => {
                  fieldRefs.current[name] = el
                }}
              />
            </div>
            <Box ref={itemListSectionRef}>
              <ItemListBox
                itemList={ewayItemList}
                setEwayItemList={setEwayItemList}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
                width: '100%',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mt: 3,
                  mb: 2,
                  fontWeight: 'bold', // Make the text bold
                }}
              >
                TRANSPORTATION DETAILS
              </Typography>
              <Typography
                fontSize={14}
                color={'rgb(39, 9, 209)'}
                sx={{
                  mt: 3,
                  mb: 0,
                }}
              >
                (Use this section when a transporter is moving goods, otherwise
                use only PART-B is enough)
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '16px',
                width: '100%',
              }}
            >
              <Box
                sx={{ flex: 1 }}
                ref={(el: HTMLDivElement | null) => {
                  fieldRefs.current['transporterId'] = el
                }}
              >
                <FormInputTextField
                  control={control}
                  name={'transporterId'}
                  label={'Transporter ID'}
                  onChange={handleTrsanporterIDChange}
                  InputProps={{ inputProps: { maxLength: 15 } }}
                />
              </Box>
              <Box
                sx={{ flex: 1 }}
                ref={(el: HTMLDivElement | null) => {
                  fieldRefs.current['transporterName'] = el
                }}
              >
                <FormInputTextField
                  control={control}
                  name={'transporterName'}
                  label={'Transporter Name'}
                />
              </Box>
              <Box
                sx={{ flex: 1 }}
                ref={(el: HTMLDivElement | null) => {
                  fieldRefs.current['distance'] = el
                }}
              >
                <FormInputTextField
                  control={control}
                  name={'distance'}
                  label={'Approximate Distance (in KM)'}
                />
              </Box>
            </Box>
            <Typography
              variant="h6"
              sx={{
                mt: 3,
                mb: 2,
                fontWeight: 'bold', // Make the text bold
              }}
            >
              PART-B
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box
                  ref={(el: HTMLDivElement | null) => {
                    fieldRefs.current['vehicleNumber'] = el
                  }}
                >
                  <FormInputTextField
                    control={control}
                    name={'vehicleNumber'}
                    label={'Vehicle No'}
                    onChange={handleVehicleNumberChange}
                    InputProps={{ inputProps: { maxLength: 15, minLength: 7 } }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  ref={(el: HTMLDivElement | null) => {
                    fieldRefs.current['transporterDoc'] = el
                  }}
                >
                  <FormInputTextField
                    control={control}
                    name={'transporterDoc'}
                    label={'Transporter Doc (Optional)'}
                  />
                </Box>
              </Grid>
              {/* <Grid item xs={12} md={4}>
                {' '}
                <FormInputDateField
                  control={control}
                  name={'transportDate'}
                  label="Date"
                />
              </Grid> */}
            </Grid>
          </Box>
        </form>
      </FormProvider>
      {(loadingStore ||
        loadingCustomer ||
        loadingEwayInfo ||
        creatingEWayBill) && <Loader />}
    </AppDialog>
  )
}
